import React, { useState, useEffect } from 'react';
import { Search, Download, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function PreadmisionNotas({ filters }) {
    const [tipoPreadmision, setTipoPreadmision] = useState('curso'); // 'curso' o 'prueba'
    const [estudiantes, setEstudiantes] = useState([]);
    const [notas, setNotas] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [cargadoNotasHabilitado, setCargadoNotasHabilitado] = useState(true);
    const [periodoInfo, setPeriodoInfo] = useState(null);
    const [inscripcionCerrada, setInscripcionCerrada] = useState(false);
    const [mensajeCierre, setMensajeCierre] = useState('');

    useEffect(() => {
        verificarPeriodoCargadoNotas();
    }, [tipoPreadmision]);

    useEffect(() => {
        if (filters.id_asignatura) {
            loadEstudiantes();
            loadNotas();
        }
    }, [filters.id_asignatura, filters.id_paralelo, filters.gestion, tipoPreadmision]);

    const verificarPeriodoCargadoNotas = async () => {
        try {
            const paramName = 'Cargado de notas Admisión (docente)';

            const response = await fetch(`${API_URL}/direccion/parametrizaciones/verificar/${encodeURIComponent(paramName)}`);
            const data = await response.json();
            
            setCargadoNotasHabilitado(data.habilitado);
            setPeriodoInfo(data);

            if (!data.habilitado) {
                setInscripcionCerrada(true);
                setMensajeCierre(data.mensaje || 'El periodo de cargado de notas de admisión se encuentra cerrado.');
            } else {
                setInscripcionCerrada(false);
            }
        } catch (error) {
            console.error('Error verificando periodo:', error);
        }
    };

    const loadEstudiantes = async () => {
        try {
            setLoading(true);
            const query = new URLSearchParams({
                id_asignatura: filters.id_asignatura,
                ...(filters.id_paralelo && { id_paralelo: filters.id_paralelo }),
                tipo: tipoPreadmision === 'prueba' ? 'admision' : 'preadmision'
            });

            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/docente/estudiantes?${query}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setEstudiantes(data.estudiantes || []);
        } catch (error) {
            console.error('Error loading estudiantes:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadNotas = async () => {
        try {
            const query = new URLSearchParams({
                tipo: tipoPreadmision,
                id_asignatura: filters.id_asignatura,
                ...(filters.id_paralelo && { id_paralelo: filters.id_paralelo }),
                gestion: filters.gestion
            });

            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/docente/notas-preadmision?${query}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();

            // Convertir array a objeto {id_estudiante: {proceso, evaluacion, nota_prueba}}
            const notasMap = {};
            (data.notas || []).forEach(n => {
                notasMap[n.id_estudiante] = {
                    proceso: n.proceso,
                    evaluacion: n.evaluacion,
                    nota_prueba: n.nota_prueba,
                    promedio: n.promedio
                };
            });
            setNotas(notasMap);
        } catch (error) {
            console.error('Error loading notas:', error);
        }
    };

    const handleNotaChange = (id_estudiante, campo, valor) => {
        setNotas(prev => ({
            ...prev,
            [id_estudiante]: {
                ...prev[id_estudiante],
                [campo]: valor ? parseFloat(valor) : null
            }
        }));
    };

    const handleGuardar = async () => {
        if (!cargadoNotasHabilitado) {
            alert(`El periodo de cargado de notas no está habilitado.${periodoInfo?.fecha_inicio ? `\nFecha inicio: ${new Date(periodoInfo.fecha_inicio).toLocaleDateString()}` : ''}${periodoInfo?.fecha_fin ? `\nFecha fin: ${new Date(periodoInfo.fecha_fin).toLocaleDateString()}` : ''}`);
            return;
        }
        try {
            setSaving(true);

            const promises = estudiantes.map(est => {
                const nota = notas[est.id_estudiante] || {};
                const token = localStorage.getItem('token');
                return fetch(`${API_URL}/docente/notas-preadmision`, {
                    method: 'POST',
                    headers: { 
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        id_estudiante: est.id_estudiante,
                        id_asignatura: filters.id_asignatura,
                        id_paralelo: filters.id_paralelo || null,
                        tipo: tipoPreadmision,
                        proceso: tipoPreadmision === 'curso' ? nota.proceso : null,
                        evaluacion: tipoPreadmision === 'curso' ? nota.evaluacion : null,
                        nota_prueba: tipoPreadmision === 'prueba' ? nota.nota_prueba : null,
                        gestion: filters.gestion
                    })
                });
            });

            await Promise.all(promises);
            alert('Notas guardadas exitosamente');
            await loadNotas();
        } catch (error) {
            console.error('Error saving notas:', error);
            alert('Error al guardar notas');
        } finally {
            setSaving(false);
        }
    };

    const filteredEstudiantes = estudiantes.filter(est =>
        searchTerm === '' ||
        est.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        est.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-4">
            {/* Mensaje si inscripciones cerradas */}
            {inscripcionCerrada ? (
                <div className="bg-white shadow-xl rounded-2xl p-8 mb-6 text-center border border-red-100">
                    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-10 h-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h2 className="text-xl font-bold text-gray-800 mb-2">Periodo de Calificación Cerrado</h2>
                    <p className="text-gray-600 mb-4">{mensajeCierre}</p>
                </div>
            ) : (
                <>
            {/* Barra de herramientas */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <label className="text-sm font-medium text-gray-700">Tipo:</label>
                    <select
                        value={tipoPreadmision}
                        onChange={(e) => setTipoPreadmision(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="curso">Curso de Preadmisión</option>
                        <option value="prueba">Prueba de Admisión</option>
                    </select>
                </div>

                <div className="flex items-center gap-2">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Buscar estudiante..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    </div>

                    <button
                        onClick={() => alert('Descarga funcionalidad')}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                        <Download size={18} />
                        Descargar
                    </button>
                </div>
            </div>

            {/* Tabla */}
            {loading ? (
                <div className="p-8 text-center text-gray-500">Cargando...</div>
            ) : filteredEstudiantes.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                    {filters.id_asignatura ? 'No hay estudiantes' : 'Selecciona una asignatura'}
                </div>
            ) : (
                <>
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="bg-gray-50 border-b">
                                    <th className="p-3 text-left text-sm font-semibold text-gray-700">No</th>
                                    <th className="p-3 text-left text-sm font-semibold text-gray-700">Código</th>
                                    <th className="p-3 text-left text-sm font-semibold text-gray-700">Email</th>

                                    {tipoPreadmision === 'curso' ? (
                                        <>
                                            <th className="p-3 text-center text-sm font-semibold text-gray-700">Proceso</th>
                                            <th className="p-3 text-center text-sm font-semibold text-gray-700">Evaluación</th>
                                            <th className="p-3 text-center text-sm font-semibold text-gray-700 bg-blue-50">Promedio</th>
                                        </>
                                    ) : (
                                        <th className="p-3 text-center text-sm font-semibold text-gray-700">Nota</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredEstudiantes.map((est, idx) => {
                                    const nota = notas[est.id_estudiante] || {};
                                    const promedio = nota.proceso && nota.evaluacion
                                        ? ((nota.proceso + nota.evaluacion) / 2).toFixed(2)
                                        : '-';

                                    return (
                                        <tr key={est.id_estudiante} className="border-b hover:bg-gray-50">
                                            <td className="p-3 text-sm">{idx + 1}</td>
                                            <td className="p-3 text-sm font-medium">{est.codigo}</td>
                                            <td className="p-3 text-sm">{est.email}</td>

                                            {tipoPreadmision === 'curso' ? (
                                                <>
                                                    <td className="p-3">
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            max="100"
                                                            step="0.01"
                                                            value={nota.proceso || ''}
                                                            onChange={(e) => handleNotaChange(est.id_estudiante, 'proceso', e.target.value)}
                                                            className="w-20 px-2 py-1 text-center border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        />
                                                    </td>
                                                    <td className="p-3">
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            max="100"
                                                            step="0.01"
                                                            value={nota.evaluacion || ''}
                                                            onChange={(e) => handleNotaChange(est.id_estudiante, 'evaluacion', e.target.value)}
                                                            className="w-20 px-2 py-1 text-center border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        />
                                                    </td>
                                                    <td className="p-3 text-center bg-blue-50">
                                                        <span className="font-semibold text-blue-700">{promedio}</span>
                                                    </td>
                                                </>
                                            ) : (
                                                <td className="p-3">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        step="0.01"
                                                        value={nota.nota_prueba || ''}
                                                        onChange={(e) => handleNotaChange(est.id_estudiante, 'nota_prueba', e.target.value)}
                                                        className="w-20 px-2 py-1 text-center border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex justify-end">
                        <button
                            onClick={handleGuardar}
                            disabled={saving || !cargadoNotasHabilitado || filteredEstudiantes.length === 0}
                            className="flex items-center gap-2 px-6 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Save size={18} />
                            {saving ? 'Guardando...' : 'Guardar Notas'}
                        </button>
                    </div>
                </>
            )}
        </>
    )}
</div>
    );
}
